import {
    Component,
    OnInit,
    OnChanges,
    SimpleChanges,
    Input,
    Output,
    EventEmitter,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    inject,
    effect,
} from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import {
    INewLeadsCreatedResponse,
    INewLeadsDataPoint,
    INewLeadsFilters,
    INewLeadsCreatedApiResponse,
    INewLeadsCreatedApiOptions,
} from '@app/core/models/chart.model'
import { AppConstants, MONTH_NAMES } from '@app/core/constants/app.constants'
import { API_ENDPOINTS } from '@app/core/constants/api.constants'
import { ELeadSourceType } from '@app/core/enum/chart.enum'
import { valueConstants } from '@app/core/constants/valueConstants'
import { environment } from 'src/environments/environment'
import { UserStore } from '@core/services/user/user.store'
import { getUserId } from '@app/core/helpers/user.helper'
import { map, catchError } from 'rxjs/operators'
import { of } from 'rxjs'
import { EiTooltipDirective } from '../../../../shared/directives'
import { IDashboardService } from '@core/services/dashboard/dashboard.service'
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexXAxis,
    ApexYAxis,
    ApexDataLabels,
    ApexTooltip,
    ApexLegend,
    ApexFill,
    ApexTheme,
    ChartComponent,
} from 'ng-apexcharts'
import { ICON_PATHS } from '@app/core/constants/assets.constants'

export interface ChartOptions {
    series: ApexAxisChartSeries
    chart: ApexChart
    xaxis: ApexXAxis
    yaxis: ApexYAxis
    dataLabels: ApexDataLabels
    tooltip: ApexTooltip
    legend: ApexLegend
    fill: ApexFill
    colors: string[]
    theme: ApexTheme
}

@Component({
    selector: 'app-new-leads-created',
    templateUrl: './new-leads-created.component.html',
    styleUrls: ['./new-leads-created.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EiTooltipDirective, ChartComponent],
})
export class NewLeadsCreatedComponent implements OnInit, OnChanges {
    private readonly http = inject(HttpClient)
    private readonly cdr = inject(ChangeDetectorRef)
    private readonly userStore = inject(UserStore)
    private readonly dashboardService = inject(IDashboardService)

    @Input() selectedCity: string = valueConstants.ALL_CITIES
    @Input() selectedRM: string = AppConstants.ALL_REPS
    @Input() selectedProduct: string = AppConstants.ALL_PRODUCTS
    @Input() rmNames: string[] = []

    @Output() cityChange = new EventEmitter<string>()
    @Output() rmChange = new EventEmitter<string>()
    @Output() productChange = new EventEmitter<string>()
    @Output() filtersLoaded = new EventEmitter<INewLeadsCreatedApiOptions>()

    chartOptions: ChartOptions | null = null
    isLoading = true
    isLoadingFilters = true
    error: string | null = null
    hasNoRecords = false
    filters: INewLeadsFilters | null = null
    filterOptions: INewLeadsCreatedApiOptions | null = null
    allData: INewLeadsDataPoint[] = []
    currentYear: number = this.dashboardService.selectedAcademicYearStart()
    chartMonths: string[] = []
    private lastAcademicYearStart: number | null = null

    readonly AppConstants = AppConstants
    readonly ELeadSourceType = ELeadSourceType
    readonly valueConstants = valueConstants
    readonly infoIcon = ICON_PATHS.INFO

    readonly CHART_COLORS = {
        [ELeadSourceType.NEW_CLIENTS]: '#E77258',
        [ELeadSourceType.EXISTING_CLIENTS]: '#5DC688',
        [ELeadSourceType.PAST_CLIENTS]: '#FFEA62',
    } as const

    constructor() {
        effect(() => {
            const academicYearStart = this.dashboardService.selectedAcademicYearStart()
            if (this.lastAcademicYearStart === null) {
                this.lastAcademicYearStart = academicYearStart
                this.currentYear = academicYearStart
                return
            }
            if (academicYearStart === this.lastAcademicYearStart) {
                return
            }
            this.lastAcademicYearStart = academicYearStart
            this.currentYear = academicYearStart
            this.loadChartData()
        })
    }

    ngOnInit(): void {
        this.loadChartData()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            (changes['selectedCity'] || changes['selectedRM'] || changes['selectedProduct']) &&
            !this.isLoadingFilters &&
            this.filterOptions
        ) {
            this.loadChartData()
        }
    }

    getTooltipText(): string {
        return `Distribution of leads by client type:\n• Past Clients – ${AppConstants.PAST_CLIENTS_DESCRIPTION}\n• Existing Clients – ${AppConstants.EXISTING_CLIENTS_DESCRIPTION}\n• New Clients – ${AppConstants.NEW_CLIENTS_DESCRIPTION}`
    }

    getTitleSuffix(): string {
        const parts: string[] = []
        if (this.selectedCity !== valueConstants.ALL_CITIES) {
            parts.push(this.selectedCity)
        }
        if (this.selectedRM !== AppConstants.ALL_REPS) {
            parts.push(this.selectedRM)
        }
        return parts.length ? ` (${parts.join(') (')})` : ''
    }

    onCityChange(city: string | number | null): void {
        const cityValue = city ? String(city) : valueConstants.ALL_CITIES
        this.selectedCity = cityValue
        this.cityChange.emit(cityValue)
        if (!this.isLoadingFilters) {
            this.loadChartData()
        }
    }

    onRMChange(rm: string | number | null): void {
        const rmValue = rm ? String(rm) : AppConstants.ALL_REPS
        this.selectedRM = rmValue
        this.rmChange.emit(rmValue)
        if (!this.isLoadingFilters) {
            this.loadChartData()
        }
    }

    onProductChange(product: string | number | null): void {
        const productValue = product ? String(product) : AppConstants.ALL_PRODUCTS
        this.selectedProduct = productValue
        this.productChange.emit(productValue)
        if (!this.isLoadingFilters) {
            this.loadChartData()
        }
    }

    private loadChartData(): void {
        const userId = getUserId(this.userStore)
        if (!userId) {
            this.handleError('User ID not available')
            return
        }

        this.isLoading = true
        this.error = null
        this.hasNoRecords = false

        const params = this.buildQueryParams(userId)
        const url = `${environment.apiBaseUrl}${API_ENDPOINTS.LEADS_CREATED}`

        this.http
            .get<INewLeadsCreatedApiResponse>(url, { params })
            .pipe(
                map((response) => {
                    if (response.success && response.data) {
                        if (response.options) {
                            this.filterOptions = response.options
                            this.isLoadingFilters = false
                            this.filtersLoaded.emit(response.options)
                        }

                        const monthlyData = response.data.monthlyBreakdown?.months || []
                        this.allData = monthlyData
                        this.hasNoRecords = this.isEmptyChartData(monthlyData)

                        this.filters = {
                            city: this.selectedCity,
                            salesRep: this.selectedRM,
                            product: this.selectedProduct,
                        }

                        return this.hasNoRecords
                            ? null
                            : this.transformDataToChartOptions(monthlyData)
                    } else {
                        throw new Error(response.message || 'Failed to load data')
                    }
                }),
                catchError((error) => {
                    const errorMessage =
                        error?.error?.message || error?.message || 'Failed to load new leads data'
                    this.handleError(errorMessage)
                    return of(null)
                }),
            )
            .subscribe({
                next: (options) => {
                    if (options) {
                        this.chartOptions = options
                    } else if (this.hasNoRecords) {
                        this.chartOptions = null
                    }
                    this.isLoading = false
                    this.cdr.markForCheck()
                },
                error: () => {
                    this.handleError('Failed to load new leads data')
                },
            })
    }

    private buildQueryParams(userId: string): HttpParams {
        let params = new HttpParams()
            .set('user_id', userId)
            .set('current_year', this.dashboardService.selectedAcademicYearStart().toString())

        const filterMappings = [
            {
                condition: this.selectedProduct !== AppConstants.ALL_PRODUCTS,
                key: 'product',
                value: this.selectedProduct,
            },
            {
                condition: this.selectedCity !== valueConstants.ALL_CITIES,
                key: 'city_name',
                value: this.selectedCity,
            },
            {
                condition: this.selectedRM !== AppConstants.ALL_REPS,
                key: 'assigned_user_name',
                value: this.selectedRM,
            },
        ]

        filterMappings.forEach(({ condition, key, value }) => {
            if (condition) {
                params = params.set(key, value)
            }
        })

        return params
    }

    private handleError(message: string): void {
        this.error = message
        this.hasNoRecords = false
        this.chartOptions = null
        this.isLoading = false
        this.isLoadingFilters = false
        this.cdr.markForCheck()
    }

    private isEmptyChartData(data: INewLeadsDataPoint[]): boolean {
        return (
            !data.length ||
            data.every((item) => item.newClients + item.existingClients + item.pastClients === 0)
        )
    }

    private generateAllMonths(startYear: number = this.currentYear, startMonthIndex = 6): string[] {
        return Array.from({ length: 12 }, (_, i) => {
            const index = (startMonthIndex + i) % 12
            const year = index >= startMonthIndex ? startYear : startYear + 1
            return `${MONTH_NAMES[index]} ${year}`
        })
    }

    private transformDataToChartOptions(data: INewLeadsDataPoint[]): ChartOptions {
        const allMonths = this.generateAllMonths(this.currentYear)

        const dataMap = new Map<string, INewLeadsDataPoint>()
        data.forEach((item) => {
            dataMap.set(item.month, item)
        })

        const months: string[] = []
        const newClientsData: number[] = []
        const existingClientsData: number[] = []
        const pastClientsData: number[] = []

        allMonths.forEach((month) => {
            months.push(month)
            const monthData = dataMap.get(month)
            if (monthData) {
                newClientsData.push(monthData.newClients)
                existingClientsData.push(monthData.existingClients)
                pastClientsData.push(monthData.pastClients)
            } else {
                newClientsData.push(0)
                existingClientsData.push(0)
                pastClientsData.push(0)
            }
        })

        // Store months array for tooltip access
        this.chartMonths = months

        const allTotals = months.map(
            (_, index) =>
                newClientsData[index] + existingClientsData[index] + pastClientsData[index],
        )
        const maxValue = allTotals.length > 0 ? Math.max(...allTotals) : 0
        const yAxisMax = maxValue > 0 ? Math.ceil(maxValue / 200) * 200 : 200

        return {
            series: [
                {
                    name: ELeadSourceType.NEW_CLIENTS,
                    data: newClientsData,
                },
                {
                    name: ELeadSourceType.EXISTING_CLIENTS,
                    data: existingClientsData,
                },
                {
                    name: ELeadSourceType.PAST_CLIENTS,
                    data: pastClientsData,
                },
            ],
            chart: {
                type: 'bar',
                height: 400,
                stacked: true,
                animations: {
                    enabled: true,
                    speed: 800,
                },
                toolbar: {
                    show: false,
                },
            },
            xaxis: {
                categories: months,
                labels: {
                    rotate: 0,
                    rotateAlways: false,
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Nunito, sans-serif',
                        fontWeight: 500,
                        colors: '#666',
                    },
                    trim: false,
                    hideOverlappingLabels: false,
                },
                axisBorder: {
                    show: false,
                },
                axisTicks: {
                    show: false,
                },
            },
            yaxis: {
                title: {
                    text: 'Number of Leads',
                    style: {
                        fontSize: '14px',
                        fontFamily: 'Nunito, sans-serif',
                        fontWeight: 600,
                    },
                },
                min: 0,
                max: yAxisMax,
                tickAmount: 5,
                labels: {
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Nunito, sans-serif',
                        fontWeight: 500,
                    },
                },
            },
            dataLabels: {
                enabled: false,
            },
            legend: {
                position: 'bottom',
                horizontalAlign: 'center',
                fontSize: '14px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 500,
                markers: {
                    strokeWidth: 0,
                    shape: 'circle',
                    offsetX: 0,
                    offsetY: 0,
                },
                itemMargin: {
                    horizontal: 15,
                    vertical: 5,
                },
            },
            fill: {
                opacity: 1,
            },
            colors: [
                this.CHART_COLORS[ELeadSourceType.NEW_CLIENTS],
                this.CHART_COLORS[ELeadSourceType.EXISTING_CLIENTS],
                this.CHART_COLORS[ELeadSourceType.PAST_CLIENTS],
            ],
            tooltip: {
                enabled: true,
                shared: true,
                followCursor: true,
                intersect: false,
                inverseOrder: true,
                theme: 'light',
                style: {
                    fontSize: '14px',
                    fontFamily: 'Nunito, sans-serif',
                },
                custom: (opts: {
                    series: number[][]
                    seriesIndex: number
                    dataPointIndex: number
                    w: { globals: { categoryLabels: string[] } }
                }) => {
                    // Get month from stored array or fallback to categoryLabels
                    const month =
                        this.chartMonths[opts.dataPointIndex] ||
                        opts.w.globals.categoryLabels?.[opts.dataPointIndex] ||
                        '-'
                    const newClients = opts.series[0]?.[opts.dataPointIndex] ?? 0
                    const existingClients = opts.series[1]?.[opts.dataPointIndex] ?? 0
                    const pastClients = opts.series[2]?.[opts.dataPointIndex] ?? 0
                    const newClientsColor = this.CHART_COLORS[ELeadSourceType.NEW_CLIENTS]
                    const existingClientsColor = this.CHART_COLORS[ELeadSourceType.EXISTING_CLIENTS]
                    const pastClientsColor = this.CHART_COLORS[ELeadSourceType.PAST_CLIENTS]
                    const salesRep = this.filters?.salesRep || AppConstants.ALL_REPS
                    const city = this.filters?.city || valueConstants.ALL_CITIES
                    const product = this.filters?.product || AppConstants.ALL_PRODUCTS

                    return `
                        <div class="custom-tooltip">
                            <div class="custom-tooltip__title">${month}</div>
                            <div class="custom-tooltip__item" style="color: ${newClientsColor}">
                                ${ELeadSourceType.NEW_CLIENTS}: ${newClients} Leads
                            </div>
                            <div class="custom-tooltip__item" style="color: ${existingClientsColor}">
                                ${ELeadSourceType.EXISTING_CLIENTS}: ${existingClients} Leads
                            </div>
                            <div class="custom-tooltip__item" style="color: ${pastClientsColor}">
                                ${ELeadSourceType.PAST_CLIENTS}: ${pastClients} Leads
                            </div>
                            <div class="custom-tooltip__footer">
                                <div class="custom-tooltip__footer-item">${AppConstants.SALES_REP_LABEL}: ${salesRep}</div>
                                <div class="custom-tooltip__footer-item">${AppConstants.CITY_LABEL}: ${city}</div>
                                <div class="custom-tooltip__footer-item">${AppConstants.PRODUCT_LABEL}: ${product}</div>
                            </div>
                        </div>
                    `
                },
                marker: {
                    show: true,
                },
            },
            theme: {
                mode: 'light',
            },
        }
    }
}
